/**
 * ServerProvisioningLogTimeline — Timeline AntD des logs de setup.
 *
 * - Mode live (SSE) si status=installing.
 * - Filtres level + step + search full-text (client-side).
 * - Highlight visuel regex erreurs.
 * - Bouton télécharger logs complets (endpoint /full-logs).
 *
 * @spec specs/app/admin/dedicated-servers.md §6.5
 */

import { Button, Input, Select, Tag, Timeline } from 'antd';
import { CircleDot, Download, RefreshCw, Search } from 'lucide-react';
import { useMemo, useState, type FC } from 'react';
import { useDedicatedServerLogs } from '../hooks/useDedicatedServerLogs';
import type { ProvisioningLog, ServerStatus } from '@/types/dedicatedServer';

interface ServerProvisioningLogTimelineProps {
  serverId: string;
  serverName: string;
  serverStatus: ServerStatus;
  onFullLogsClick: () => void;
  onDownloadClick: () => void;
}

const LEVEL_COLOR: Record<string, string> = {
  info: 'blue',
  warn: 'orange',
  error: 'red',
  debug: 'default',
};

const ERROR_REGEX = /(error|fatal|panic|exit code [1-9])/i;

const LEVEL_OPTIONS = [
  { value: 'info', label: 'Info' },
  { value: 'warn', label: 'Warn' },
  { value: 'error', label: 'Error' },
  { value: 'debug', label: 'Debug' },
];

const ServerProvisioningLogTimeline: FC<ServerProvisioningLogTimelineProps> = ({
  serverId,
  serverName,
  serverStatus,
  onFullLogsClick,
  onDownloadClick,
}) => {
  const { logs, isLoading, isStreaming } = useDedicatedServerLogs(serverId, serverStatus);

  const [levels, setLevels] = useState<string[]>([]);
  const [steps, setSteps] = useState<string[]>([]);
  const [query, setQuery] = useState('');

  const stepOptions = useMemo(() => {
    const set = new Set<string>();
    logs.forEach((l) => {
      if (l.step) set.add(l.step);
    });
    return Array.from(set).map((s) => ({ value: s, label: s }));
  }, [logs]);

  const filtered = useMemo(() => {
    return logs.filter((log) => {
      if (levels.length > 0 && !levels.includes(log.level)) return false;
      if (steps.length > 0 && log.step && !steps.includes(log.step)) return false;
      if (query.trim() && !log.output.toLowerCase().includes(query.toLowerCase())) return false;
      return true;
    });
  }, [logs, levels, steps, query]);

  const items = useMemo(
    () =>
      filtered.map<{ color: string; children: React.ReactNode; key: string }>((log, index) => ({
        key: `${log.created_at}-${index}`,
        color: LEVEL_COLOR[log.level] ?? 'blue',
        children: <LogEntry log={log} />,
      })),
    [filtered],
  );

  return (
    <div className="fc-card p-5">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <h3 className="text-sm font-semibold text-slate-900 dark:text-white">
            Logs de provisioning
          </h3>
          {isStreaming && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-red-50 px-2 py-0.5 text-[11px] font-medium text-red-600 dark:bg-red-950/40 dark:text-red-300">
              <CircleDot className="h-3 w-3 animate-pulse" />
              En direct
            </span>
          )}
          {!isStreaming && (
            <span className="text-[11px] text-slate-500 dark:text-slate-400">
              {serverName} · {filtered.length} entrée{filtered.length > 1 ? 's' : ''}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="small"
            icon={<Download className="h-3.5 w-3.5" />}
            onClick={onDownloadClick}
          >
            Télécharger .txt
          </Button>
          <Button
            size="small"
            icon={<RefreshCw className="h-3.5 w-3.5" />}
            onClick={onFullLogsClick}
          >
            Tous les logs (drawer)
          </Button>
        </div>
      </div>

      <div className="mb-4 grid grid-cols-1 gap-2 md:grid-cols-3">
        <Select
          mode="multiple"
          allowClear
          placeholder="Filtrer par niveau"
          value={levels}
          onChange={setLevels}
          options={LEVEL_OPTIONS}
          size="small"
        />
        <Select
          mode="multiple"
          allowClear
          placeholder="Filtrer par étape"
          value={steps}
          onChange={setSteps}
          options={stepOptions}
          size="small"
        />
        <Input
          placeholder="Rechercher dans output…"
          prefix={<Search className="h-3.5 w-3.5 text-slate-400" />}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          size="small"
          allowClear
        />
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-8 text-sm text-slate-400">
          Chargement des logs…
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex items-center justify-center py-8 text-sm text-slate-400">
          Aucun log à afficher.
        </div>
      ) : (
        <div className="max-h-[600px] overflow-auto">
          <Timeline mode="left" items={items} />
        </div>
      )}
    </div>
  );
};

const LogEntry: FC<{ log: ProvisioningLog }> = ({ log }) => {
  const [expanded, setExpanded] = useState(false);
  const isError = ERROR_REGEX.test(log.output);
  const truncated = log.output.length > 200;
  const displayed = expanded || !truncated ? log.output : `${log.output.slice(0, 200)}…`;
  const timeStr = new Date(log.created_at).toLocaleTimeString('fr-FR');

  return (
    <div className="space-y-1">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-mono text-[11px] text-slate-500 dark:text-slate-400">{timeStr}</span>
        {log.step && (
          <Tag color="geekblue" className="text-[11px]">
            {log.step}
          </Tag>
        )}
        <Tag color={LEVEL_COLOR[log.level] ?? 'blue'} className="text-[11px]">
          {log.level.toUpperCase()}
        </Tag>
      </div>
      <pre
        className={`whitespace-pre-wrap break-words rounded-md border p-2 font-mono text-[11px] ${
          isError
            ? 'border-red-200 bg-red-50 text-red-900 dark:border-red-900 dark:bg-red-950/40 dark:text-red-200'
            : 'border-slate-200 bg-slate-50 text-slate-800 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200'
        }`}
      >
        {displayed}
      </pre>
      {truncated && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="text-[11px] font-medium text-blue-600 hover:underline dark:text-blue-400"
        >
          {expanded ? 'Réduire' : 'Afficher tout'}
        </button>
      )}
    </div>
  );
};

export default ServerProvisioningLogTimeline;
